import { usePermissionsStore } from '@/store/permissionsStore'

/**
 * Hook pour vérifier les permissions de l'utilisateur
 */
export const usePermissions = () => {
  const {
    permissions,
    roles,
    hasPermission,
    hasAllPermissions,
    hasAnyPermission,
    hasRole,
    hasAnyRole,
    isSuperAdmin,
    isTechnicien,
    canAccessSystemControl,
    loadPermissions,
    clearPermissions,
  } = usePermissionsStore()

  return {
    permissions,
    roles,
    hasPermission,
    hasAllPermissions,
    hasAnyPermission,
    hasRole,
    hasAnyRole,
    isSuperAdmin,
    isTechnicien,
    canAccessSystemControl,
    loadPermissions,
    clearPermissions,
  }
}

/**
 * Hook pour obtenir les capacités d'action sur une ressource
 * @param {string} resource - Type de ressource ('posts', 'videos', 'banners', etc.)
 */
export const useResourcePermissions = (resource) => {
  const { hasPermission } = usePermissions()

  const getPermissionCode = (action) => {
    return `${action}_${resource}`
  }

  return {
    canView: hasPermission(getPermissionCode('view')),
    canCreate: hasPermission(getPermissionCode('create')),
    canEdit: hasPermission(getPermissionCode('edit')),
    canDelete: hasPermission(getPermissionCode('delete')),
    canArchive: hasPermission(getPermissionCode('archive')),
    canApprove: hasPermission(getPermissionCode('approve')),
  }
}

/**
 * Hook pour vérifier les permissions liées aux clients
 */
export const useClientPermissions = () => {
  const { hasPermission } = usePermissions()

  return {
    canViewClients: hasPermission('view_clients'),
    canCreateClient: hasPermission('create_client'),
    canEditClient: hasPermission('edit_client'),
    canDeleteClient: hasPermission('delete_client'),
    canArchiveClient: hasPermission('archive_client'),
  }
}

/**
 * Hook pour vérifier les permissions liées aux utilisateurs (staff)
 */
export const useUserPermissions = () => {
  const { hasPermission } = usePermissions()

  return {
    canViewUsers: hasPermission('view_users'),
    canCreateUser: hasPermission('create_user'),
    canEditUser: hasPermission('edit_user'),
    canDeleteUser: hasPermission('delete_user'),
    canArchiveUser: hasPermission('archive_user'),
  }
}

/**
 * Hook pour vérifier l'accès aux pages du dashboard admin
 */
export const useAdminAccess = () => {
  const { 
    hasPermission, 
    isSuperAdmin, 
    hasAnyRole,
    canAccessSystemControl 
  } = usePermissions()

  return {
    // Pages principales
    canAccessDashboard: hasPermission('view_dashboard'),
    canAccessClients: hasPermission('view_clients'),
    canAccessUsers: hasPermission('view_users'),
    canAccessPublications: hasPermission('view_posts'),
    canAccessVideos: hasPermission('view_videos'),
    canAccessBanners: hasPermission('view_banners'),
    canAccessCategories: hasPermission('view_categories'),
    canAccessMessages: hasPermission('view_messages'),
    canAccessActivities: hasPermission('view_activities'),
    canAccessSettings: hasPermission('view_settings'),
    
    // Pages système
    canAccessSystemControl: canAccessSystemControl(),
    
    // Vérifications de rôle
    isAdmin: hasAnyRole(['Super Admin', 'Admin']),
    isSuperAdmin: isSuperAdmin(),
    isStaff: hasAnyRole(['Super Admin', 'Admin', 'Superviseur', 'Animateur', 'Technicien']),
  }
}

export default usePermissions
